// The engine session: one place that opens the map, joins the mesh, and
// turns a capability link into a store a screen can read.
//
// Callers never touch the engine. They open a session, follow a link, and
// subscribe. That is not tidiness — every honest claim in this product is
// derived in one place from one snapshot, and a component that could read
// `subscriber.latest()` itself is a component that can render half of it.
//
// **A routing map is optional and its absence is a designed state.** Route
// graphs are regional and served from a host the operator configures;
// there is no world graph. Without one, three things are true at once and
// the session says so rather than throwing: the thread's topic tag is
// derived from the graph's own epoch (§4.2), so the capability cannot even
// be opened; `locate()` cannot place a position; and `matrix()` cannot
// time a leg. What the app shows in that state is the subject of
// `MapClaim` in honesty.ts.

import {
  arrivalClaim,
  floorClaim,
  runStatus,
  type ArrivalClaim,
  type MapClaim,
  type RunStatus
} from "./honesty.js";
import type { RecipientStop } from "./recipient.js";
import {
  loadEngine,
  loadTransport,
  type EngineBundles,
  type LatLon,
  type MeshHost,
  type MeshSession,
  type RouteEngine,
  type ThreadChannel,
  type ThreadFollow
} from "./engine.js";
import { datasetEpoch } from "./graphs.js";
import type { RunPlan, ThreadUpdate } from "./protocol.js";
import { createStore, type ReadableStore } from "./store.js";

export interface SessionOptions {
  /** Where the engine bundles are served. `/rangefind/` by default. */
  engineBaseUrl?: string;
  /**
   * Where the route graph for this region is served. **Absent by
   * default**: there is no world route graph, and inventing a fixture
   * would make the honest no-map state unreachable in development.
   */
  routeGraphUrl?: string | null;
  /**
   * The dataset this graph is a build of — `quebec/car`.
   *
   * When present, the run's epoch is derived from this name rather than
   * from the graph's contents, so the topic namespace survives a rebuild
   * of the underlying map. Absent means the old behaviour: the epoch is
   * the content hash, which is still what an operator pinning their own
   * build wants. See `datasetEpoch` in graphs.ts for why this exists.
   */
  datasetId?: string | null;
  /** A peer id for this device on the mesh. */
  id?: string;
  /** Multiaddrs from a `?b=` hint, when the page was opened with one. */
  bootstrap?: string[];
  /** Names the graph this device holds, so an ETA can claim a match (§9.2). */
  engineMode?: string | null;
}

/** Everything the session knows about itself, as one readable value. */
export interface SessionSnapshot {
  phase: "opening" | "ready" | "failed";
  map: MapClaim;
  /** True when the mesh is in-tab peers rather than a real transport. */
  loopback: boolean;
  /** Live traffic aggregates this device currently holds (§12 rows 1 and 3). */
  hasTraffic: boolean;
  message: string | null;
}

export interface FollowSnapshot {
  status: RunStatus;
  arrival: ArrivalClaim;
  /**
   * Where to draw the vehicle — **only** when the position claim is
   * `live`. Every other claim yields null here, so a map physically
   * cannot draw a pin for a coarse run or a stale fix.
   */
  position: LatLon | null;
  plan: RunPlan | null;
  myStopIndex: number | null;
  /** This recipient's own stop, when their copy of the link carried one. */
  recipient: RecipientStop | null;
  /**
   * The road the floor was measured along — van to this door, ignoring
   * the stops between. Present only in the §20.2 case, and only while
   * there is a live position to measure from, so it can never outlive
   * the claim it illustrates.
   */
  legGeometry: [number, number][];
  /** The whole run's arrival claims, by stop index, when a plan is held. */
  updatedAtMillis: number;
}

/** One published proof, as §20.7.1's accumulator vouches for it. */
export interface PhotoCommitment {
  /** 1-based position in the plan — which door this was taken at. */
  stopIndex: number;
  /** Lowercase hex of the sealed blob. Addresses it, and checks it. */
  commitment: string;
}

export interface FollowHandle {
  store: ReadableStore<FollowSnapshot>;
  /** Pull whatever this device missed from other peers (§5.5). */
  catchUp(): Promise<number>;
  /** Re-derive now, without waiting for the next record. */
  refresh(): Promise<void>;
  /**
   * Proof of delivery, as far as a *follower* can take it: the
   * commitments a run published, and the sealed bytes behind one. Both
   * are useless without the run seed, which is why this is safe to hand
   * every follower — the customer's page holds this and still cannot
   * open a photo, while the console that minted the ticket can.
   */
  proof: {
    /**
     * Commitments, checked against the chain in the newest record. Each
     * entry names the stop it was taken at, so a caller wanting one
     * door's proof picks it rather than trying keys until one works.
     */
    list(accumulator?: string | null): Promise<{
      matchedAt: number;
      verified: PhotoCommitment[];
      unverified: PhotoCommitment[];
    }>;
    /** The sealed blob for one commitment, or null if nobody has it. */
    fetch(hash: string): Promise<Uint8Array | null>;
  };
  stop(): void;
}

export interface WayfindSession {
  store: ReadableStore<SessionSnapshot>;
  bundles: EngineBundles;
  /** The route graph, when one was configured and opened. */
  engine: RouteEngine | null;
  /**
   * The epoch this session actually rendezvouses on, as 64-char hex.
   *
   * **Anything minting a ticket must read this rather than
   * `engine.root.sourceHash`.** They are the same string until a dataset
   * is configured and different afterwards, which is the failure mode
   * worth naming: a console that keeps reading the content hash still
   * mints valid tickets, so nothing breaks loudly — those jobs just
   * quietly lose the rebuild-survival the dataset was configured for.
   */
  epochHex: string | null;
  channel: ThreadChannel | null;
  mesh: MeshSession | null;
  network: unknown;
  /**
   * A loopback network of this tab's own, for a demonstration, plus the
   * channel that subscribes on it. Created on first use and null when
   * there is no map to publish onto.
   *
   * A demo needs a publisher **and** a subscriber that can hear it, and
   * on a wire session the page's own network cannot host both: a libp2p
   * host takes one MeshNode, and the viewer's already holds it. Sharing
   * the wire network was fine when the only transport was loopback, and
   * became "One MeshNode per libp2p host." on the front page the day a
   * keeper was configured — which is every deployment now.
   */
  demoBus(): Promise<{ network: unknown; channel: ThreadChannel } | null>;
  follow(
    link: string | Uint8Array,
    options?: {
      plan?: RunPlan | null;
      myStopIndex?: number | null;
      demo?: boolean;
      /** The recipient's own stop, from their copy of the link (§20.2). */
      recipient?: RecipientStop | null;
    }
  ): Promise<FollowHandle>;
  close(): Promise<void>;
}

const REFRESH_INTERVAL_MS = 1000;

/**
 * Opens the map (if there is one), joins the mesh, and returns a session.
 *
 * Never throws for a missing route graph. A missing graph is the product
 * working as designed in a region nobody has published one for, and a
 * thrown error would turn that into a broken page.
 */
export async function createWayfindSession(options: SessionOptions = {}): Promise<WayfindSession> {
  const bundles = await loadEngine(options.engineBaseUrl);
  const store = createStore<SessionSnapshot>({
    phase: "opening",
    map: { kind: "none", reason: "not-configured" },
    loopback: true,
    hasTraffic: false,
    message: null
  });

  let engine: RouteEngine | null = null;
  let map: MapClaim = { kind: "none", reason: "not-configured" };
  let message: string | null = null;

  // The epoch the mesh rendezvouses on. Derived from the dataset name
  // when there is one, so a rebuild of the map does not rename every
  // running job's channel; the content hash stays available underneath
  // for `assertGraphEpoch`, which is a different question.
  let epochHex: string | null = null;

  if (options.routeGraphUrl) {
    try {
      engine = await bundles.route.openRouteGraphUrl(options.routeGraphUrl);
      epochHex = options.datasetId
        ? await datasetEpoch(options.datasetId)
        : engine.root.sourceHash;
      map = {
        kind: "graph",
        profile: options.engineMode ?? engine.root.profile ?? null,
        epoch: epochHex
      };
    } catch (error) {
      engine = null;
      map = { kind: "none", reason: "unreachable" };
      message = `No routing map loaded from ${options.routeGraphUrl}: ${errorText(error)}`;
    }
  }

  // Without a graph there is no epoch, and without an epoch there is no
  // topic tag to subscribe to (§4.2). The session still opens — the app
  // has a designed state for this — but it holds no mesh and no channel.
  let mesh: MeshSession | null = null;
  let network: unknown = null;
  let channel: ThreadChannel | null = null;
  let host: MeshHost | null = null;
  // Loopback means "the only peers are in this tab". It is the truth for a
  // demo and a lie for a followed run, and the Provenance strip reads it,
  // so it is derived from what actually happened rather than assumed.
  let loopback = true;

  if (engine) {
    // A `?b=` hint is the difference between a page that can reach a real
    // driver and one that can only replay its own demo. With one, dial it
    // and join the wire; without one, there is genuinely nobody to talk
    // to and loopback is the honest transport.
    const bootstrap = (options.bootstrap ?? []).filter(address => String(address ?? "").trim());
    if (bootstrap.length) {
      try {
        const transport = await loadTransport(options.engineBaseUrl);
        host = await transport.createPulseMeshHost({
          profile: "browser",
          bootstrapPeers: bootstrap,
          // Content routing is how a link finds its run (threads §4.2).
          // Without it a tab and a driver's phone both connect to the same
          // keeper, neither is ever told where the other is, and nothing
          // crosses: the keeper relays, but only between peers that have
          // already found each other. It is off by default in the library
          // because the traffic channel finds peers by zone — a followed
          // thread has no zone to be found by.
          //
          // `scope` matters and its default is wrong here. The public
          // mapper strips private addresses, which is right against the
          // internet and fatal on a depot LAN or one laptop: peers connect
          // happily and never enter each other's routing tables. So it
          // follows the bootstrap we were actually handed.
          dht: { scope: privateBootstrap(bootstrap) ? "lan" : "public" }
        });
        const wire = transport.createLibp2pNetwork(host);
        await wire.ready;
        network = wire;
        loopback = false;
      } catch (error) {
        // A keeper that will not answer is a degraded network, not a
        // broken page: the run still opens, the transport stays loopback,
        // and `message` says why rather than the screen implying it
        // reached something.
        host = null;
        network = null;
        message = `Could not reach the mesh: ${errorText(error)}`;
      }
    }
    if (!network) network = bundles.pulsemesh.createLoopbackNetwork();
    mesh = await bundles.pulsemesh.createMeshSession({
      engine,
      network,
      // The engine would otherwise fall back to `root.sourceHash`. Passing
      // it here is the whole of the change: `createMeshSession` stores it
      // on the node, and `createThreadChannel` reads `node.epochHex`, so
      // the thread topic follows without being told twice.
      epochHex,
      id: options.id ?? "viewer",
      transport: loopback ? "loopback" : "wire",
      readOnly: true
    });
    mesh.start();
    channel = bundles.threads.createThreadChannel({
      node: (mesh as unknown as { node: unknown }).node,
      network,
      engine,
      // **Without the host there is no discovery at all**, and that is
      // the difference between a link that finds its run and one that
      // sits on a keeper forever. §4.2 is "a link is a key, not a
      // location": the holder derives the rendezvous key from the
      // capability, asks who provides it, and dials them. All of that
      // hangs off `host.contentRouting`, so omitting the host does not
      // degrade discovery — it removes it, silently, leaving a page that
      // is correctly wired to a peer that is not the one publishing.
      host,
      engineMode: options.engineMode ?? engine.root.profile ?? null,
      id: options.id ?? "viewer"
    });
  }

  // The demonstration's own bus, built on first use. A demo is a real
  // publisher and a real subscriber talking over a loopback network in
  // one tab — everything above this line stays on whatever transport the
  // page actually joined, and neither can see the other.
  let demo: { network: unknown; channel: ThreadChannel; mesh: MeshSession } | null = null;
  let demoOpening: Promise<{ network: unknown; channel: ThreadChannel } | null> | null = null;

  async function demoBus(): Promise<{ network: unknown; channel: ThreadChannel } | null> {
    // Without a graph there is no epoch and so no topic to publish onto,
    // which is the same reason the page holds no channel at all.
    if (!engine) return null;
    if (demo) return { network: demo.network, channel: demo.channel };
    // One bus per tab even if two callers race for it: a second would be
    // a second network, and the pair on it would never meet.
    demoOpening ??= (async () => {
      const demoNetwork = bundles.pulsemesh.createLoopbackNetwork();
      const demoMesh = await bundles.pulsemesh.createMeshSession({
        engine,
        network: demoNetwork,
        epochHex,
        id: "demo-viewer",
        transport: "loopback",
        readOnly: true
      });
      demoMesh.start();
      const demoChannel = bundles.threads.createThreadChannel({
        node: (demoMesh as unknown as { node: unknown }).node,
        network: demoNetwork,
        engine,
        // No host, and none is wanted: discovery is how a link finds a
        // run it cannot see, and on a loopback bus both ends are already
        // in the room.
        engineMode: options.engineMode ?? engine.root.profile ?? null,
        id: "demo-viewer"
      });
      demo = { network: demoNetwork, channel: demoChannel, mesh: demoMesh };
      return { network: demoNetwork, channel: demoChannel };
    })();
    return demoOpening;
  }

  function trafficCount(): number {
    const snapshot = (mesh as unknown as { snapshot?: () => { segments?: number } })?.snapshot?.();
    return snapshot?.segments ?? 0;
  }

  function publishSession(): void {
    store.set({
      phase: engine ? "ready" : "failed",
      map,
      loopback,
      hasTraffic: trafficCount() > 0,
      message
    });
  }
  publishSession();

  const follows = new Set<{ stop: () => void }>();

  async function follow(
    link: string | Uint8Array,
    followOptions: {
      plan?: RunPlan | null;
      myStopIndex?: number | null;
      demo?: boolean;
      /**
       * The recipient's own stop, out of their copy of the link. Enough
       * for a §20.2 floor when there is no plan — which is *every* real
       * dispatched run, because a plan never reaches a customer.
       */
      recipient?: RecipientStop | null;
    } = {}
  ): Promise<FollowHandle> {
    const plan = followOptions.plan ?? null;
    const recipient = followOptions.recipient ?? null;
    // A recipient card names the stop it belongs to, so a page that was
    // given one does not need to be told the index twice.
    const myStopIndex = followOptions.myStopIndex ?? recipient?.stopIndex ?? null;
    const isDemo = followOptions.demo ?? false;
    // A demonstration is followed on the demo bus, where its publisher
    // is; the page's own channel is on the wire and would hear nothing.
    const followChannel = isDemo ? (await demoBus())?.channel ?? null : channel;

    // No map, no capability: the tag is derived from the epoch, so there
    // is genuinely nothing to subscribe to. The follow still exists, and
    // says exactly that, rather than the page showing a spinner forever.
    if (!followChannel) {
      const snapshot: FollowSnapshot = {
        status: runStatus({ status: null, update: null, map, demo: isDemo, myStopIndex }),
        arrival: arrivalClaim({ raw: null, outcomes: [], myStopIndex, map }),
        position: null,
        plan,
        myStopIndex,
        recipient,
        legGeometry: [],
        updatedAtMillis: Date.now()
      };
      const dead = createStore(snapshot);
      return {
        store: dead,
        catchUp: async () => 0,
        refresh: async () => {},
        // No channel, so no peer to ask and nothing published to ask
        // about. Empty rather than absent: a caller checking for proof
        // should get "none" here and not a missing method.
        proof: {
          list: async () => ({ matchedAt: 0, verified: [], unverified: [] }),
          fetch: async () => null
        },
        stop: () => {}
      };
    }

    // `channel.follow` calls `onUpdate` **before it returns**: it runs a
    // §5.5 catch-up round as part of joining, and a record recovered there
    // is delivered immediately. So this cannot be a `const` assigned from
    // the await — the callback fires while the right-hand side is still
    // being evaluated, and `derive()` would read `latest` off `undefined`.
    let followHandle: ThreadFollow | null = null;
    try {
      followHandle = await followChannel.follow(link, { onUpdate: () => { void derive(); } });
    } catch (error) {
      throw new Error(`That link could not be opened: ${errorText(error)}`);
    }
    const opened = followHandle;

    const snapshotStore = createStore<FollowSnapshot>({
      status: runStatus({ status: null, update: null, map, demo: isDemo, myStopIndex }),
      arrival: { kind: "none" },
      position: null,
      plan,
      myStopIndex,
      recipient,
      legGeometry: [],
      updatedAtMillis: Date.now()
    });

    // The newest record that actually carried a position. A **mark**
    // carries none (§5.2.1 — `markStop` seals with no match), so without
    // this the screen would flip to a stops-only run the instant a driver
    // taps Delivered, and flip back on the next heartbeat.
    let positioned: ThreadUpdate | null = null;

    async function derive(): Promise<void> {
      // Called from `onUpdate`, which can fire before `follow()` resolves.
      if (!followHandle) return;
      const nowMillis = Date.now();
      const update: ThreadUpdate | null = followHandle.latest();
      if (update?.segment) positioned = update;
      const hasTraffic = trafficCount() > 0;
      const status = runStatus({
        status: followHandle.status({ nowMillis, hasTraffic }),
        update,
        positioned,
        map,
        demo: isDemo,
        myStopIndex,
        nowMillis
      });

      // A position is resolved **only** for a live claim. Every other
      // claim leaves `position` null, so no map component can draw a pin
      // for a coarse run or a stale fix even by accident.
      let position: LatLon | null = null;
      if (status.position.kind === "live" && engine) {
        position = await engine
          .locate(status.position.segment, status.position.ratio)
          .catch(() => null);
      }

      let arrival: ArrivalClaim;
      let legGeometry: [number, number][] = [];
      if (!plan && recipient && myStopIndex != null) {
        // The real dispatched case. No plan will ever arrive — §20.8 sees
        // to that — but the recipient's own door plus a live position is
        // enough for a floor, and a floor is an answer where "Not yet
        // known" was the only thing this page could say before.
        //
        // `position` is non-null only for a live claim, so this inherits
        // the same rule the map pin obeys: no fix, no number.
        let seconds: number | null = null;
        if (position && engine) {
          // Geometry as well as time: the same leg the number came from
          // is the only road this page has any business drawing, and
          // asking twice would let the picture and the claim disagree.
          const leg = await engine
            .route({
              from: position,
              to: { lat: recipient.lat, lon: recipient.lon },
              geometry: true,
              live: liveProvider()
            })
            .catch(() => null);
          seconds = leg ? leg.seconds : null;
          legGeometry = leg?.geometry ?? [];
        }
        arrival = floorClaim({
          seconds,
          outcomes: status.outcomes,
          myStopIndex,
          map,
          metric: hasTraffic ? "live-traffic" : "static-metric",
          profile: { basis: "unstated", graph: engine?.root.profile ?? null, run: null },
          stale: status.observed.stale,
          observationAgeSeconds: status.observed.ageSeconds ?? 0,
          nowMillis
        });
      } else if (!plan || myStopIndex == null) {
        arrival = arrivalClaim({ raw: null, outcomes: status.outcomes, myStopIndex, map });
      } else {
        const raw = await followHandle
          .eta({ plan, myStopIndex, live: liveProvider(), nowMillis })
          .catch(() => null);
        arrival = arrivalClaim({ raw, outcomes: status.outcomes, myStopIndex, map });
      }

      snapshotStore.set({
        status,
        arrival,
        position,
        plan,
        myStopIndex,
        recipient,
        legGeometry,
        updatedAtMillis: nowMillis
      });
      publishSession();
    }

    // The clock keeps moving even when no record arrives, and staleness
    // is a function of the clock. A UI that only re-derived on receipt
    // would show a 4-minute-old fix as live indefinitely.
    const timer = setInterval(() => { void derive(); }, REFRESH_INTERVAL_MS);
    await derive();

    const handle: FollowHandle = {
      store: snapshotStore,
      catchUp: () => opened.catchUp(),
      refresh: derive,
      // §20.7 passthrough. Sealed bytes and commitments only — opening a
      // photo needs the run seed, which lives in a dispatch ticket and
      // never in a follow. A holder of one of those (the console that
      // minted it) opens what this hands back; a customer's page cannot,
      // and asking is how it would find out.
      proof: {
        list: accumulator => opened.fetchPhotoList(accumulator ?? null),
        fetch: hash => opened.fetchPhoto(hash)
      },
      stop() {
        clearInterval(timer);
        opened.stop();
        follows.delete(handle);
      }
    };
    follows.add(handle);
    return handle;
  }

  function liveProvider(): unknown {
    const provider = (mesh as unknown as { provider?: () => unknown })?.provider?.();
    // §12 rows 1 and 3 need traffic that actually exists. An empty
    // provider produces the static metric, and the estimate must say so
    // — so hand it null rather than an aggregate with nothing in it.
    return trafficCount() > 0 ? provider ?? null : null;
  }

  return {
    store,
    bundles,
    engine,
    epochHex,
    channel,
    mesh,
    network,
    demoBus,
    follow,
    async close() {
      for (const entry of [...follows]) entry.stop();
      channel?.close();
      // The demo bus, when this tab ever built one. It holds a node and
      // its subscriptions like any other, and leaking it across a reload
      // would leave a second viewer talking to nobody.
      demo?.channel.close();
      await demo?.mesh.close().catch(() => {});
      await mesh?.close().catch(() => {});
      // The libp2p host outlives the mesh session and holds open sockets,
      // so closing the session has to close it too or a reload leaks a
      // connection to the keeper on every open.
      await host?.stop().catch(() => {});
    }
  };
}

function errorText(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}

/**
 * Whether every bootstrap address we were given is on a private network.
 *
 * Decides the DHT's address mapper. Conservative on purpose: one public
 * address means "public", because treating a real internet peer as LAN
 * keeps addresses in routing tables that should have been stripped, and
 * that is a privacy leak rather than a connectivity bug.
 */
function privateBootstrap(addresses: string[]): boolean {
  const isPrivate = (address: string): boolean =>
    /\/ip4\/(127\.|10\.|192\.168\.|172\.(1[6-9]|2\d|3[01])\.)/u.test(address)
    || address.includes("/ip6/::1")
    || address.includes("/dns4/localhost");
  return addresses.length > 0 && addresses.every(isPrivate);
}
